import { useState, useEffect } from 'react';
import { apiFetch } from '../api/client';
import { Settings, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { HarmonicSeriesCell, HarmonicCellData } from './HarmonicSeriesCell';
import { freshnessStatus, CADENCE, resolveIndicatorColor } from '../lib/freshness';
import { DataUnavailable } from './DataState';

interface BearingHealthRow {
  machine_id: string;
  status: string;
  colour: string;
  summary_line: string;
  mitigating: string | null;
  confidence: string;
  has_pv_alarm: boolean;
  worst_maxpk_g: number | null;
  worst_maxpk_severity: string | null;
  worst_maxpk_point: string | null;
  latest_maxpk_g: number | null;
  latest_maxpk_severity: string | null;
  latest_point_index: number | null;
  latest_point_name: string | null;
  latest_axis_num: number | null;
  calculated_at: string;
  machine_name: string;
  area_name: string;
  company_name: string;
  db_id: number;
  periodic_avg_pct: number | null;
  latest_concentration: number | null;
  latest_harmonic_fraction: number | null;
}

const STATUS_ORDER: Record<string, number> = {
  Urgent: 1, Action: 2, Investigate: 3, Monitoring: 4, Healthy: 5,
};

function fmtUtcToEastern(ts: string): string {
  if (!ts) return '—';
  let s = ts.trim().replace(' ', 'T');
  if (!/Z$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function ColTooltip({ text }: { text: string }) {
  return (
    <span className="relative group inline-flex items-center ml-0.5" onClick={e => e.stopPropagation()}>
      <span className="w-3.5 h-3.5 rounded-full bg-gray-300 text-white text-[9px] font-bold flex items-center justify-center cursor-help leading-none shrink-0">?</span>
      <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-gray-800 text-white text-xs rounded px-2.5 py-2 leading-snug opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-lg whitespace-normal text-left font-normal normal-case tracking-normal">
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-800" />
        {text}
      </span>
    </span>
  );
}

function ConfidencePip({ level }: { level: string }) {
  const colors = { high: '#16a34a', medium: '#d97706', low: '#94a3b8' };
  const color = colors[level as keyof typeof colors] ?? '#94a3b8';
  return (
    <span className="inline-flex items-center gap-1 text-xs" style={{ color }}>
      <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: color }} />
      {level}
    </span>
  );
}

type SortKey = 'status' | 'machine_name' | 'worst_maxpk_g' | 'confidence' | 'periodic_avg_pct' | 'latest_concentration' | 'latest_harmonic_fraction' | 'calculated_at';

const AXIS_LABEL: Record<number, string> = { 1: 'H', 2: 'V', 3: 'A' };

interface Props {
  onMachineClick: (machineId: string, machineName: string, points: any[], imageUrl?: string, initialPointName?: string) => void;
}

export function BearingHealth({ onMachineClick }: Props) {
  const [rows, setRows] = useState<BearingHealthRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dbFilter, setDbFilter] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('status');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [expandedSummaries, setExpandedSummaries] = useState<Set<string>>(new Set());
  const [harmonicColumn, setHarmonicColumn] = useState<Record<string, HarmonicCellData>>({});
  const [maxpkRoc, setMaxpkRoc] = useState<Record<string, { roc_state: string; slope_pct_wk: number | null; r2: number | null; n_captures: number }>>({});

  const toggleSummary = (id: string) => {
    setExpandedSummaries(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  useEffect(() => {
    setLoading(true);
    setError(false);
    apiFetch<BearingHealthRow[]>('/machines/bearing-health')
      .then(data => { setRows(data || []); setError(false); })
      .catch(() => { setRows([]); setError(true); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    apiFetch<Record<string, HarmonicCellData>>('/machines/harmonic-column')
      .then(data => setHarmonicColumn(data || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    apiFetch<typeof maxpkRoc>('/machines/maxpk-roc')
      .then(data => setMaxpkRoc(data || {}))
      .catch(() => {});
  }, []);

  const databases = Array.from(
    new Map(rows.map(r => [r.db_id, r.company_name || String(r.db_id)])).entries()
  ).sort((a, b) => a[1].localeCompare(b[1]));

  const STATUSES = ['all', 'Urgent', 'Action', 'Investigate', 'Monitoring', 'Healthy'];

  const filtered = rows
    .filter(r => statusFilter === 'all' || r.status === statusFilter)
    .filter(r => dbFilter === null || r.db_id === dbFilter);

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'status') cmp = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
    else if (sortKey === 'machine_name') cmp = a.machine_name.localeCompare(b.machine_name);
    else if (sortKey === 'worst_maxpk_g') cmp = (b.latest_maxpk_g ?? 0) - (a.latest_maxpk_g ?? 0);
    else if (sortKey === 'periodic_avg_pct') cmp = (b.periodic_avg_pct ?? -1) - (a.periodic_avg_pct ?? -1);
    else if (sortKey === 'latest_concentration') cmp = (b.latest_concentration ?? -1) - (a.latest_concentration ?? -1);
    else if (sortKey === 'latest_harmonic_fraction') cmp = (b.latest_harmonic_fraction ?? -1) - (a.latest_harmonic_fraction ?? -1);
    else if (sortKey === 'confidence') {
      const confOrder: Record<string, number> = { high: 1, medium: 2, low: 3 };
      cmp = (confOrder[a.confidence] ?? 4) - (confOrder[b.confidence] ?? 4);
    }
    else if (sortKey === 'calculated_at') cmp = a.calculated_at.localeCompare(b.calculated_at);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortHeader = ({ col, label, className = '', tooltip }: { col: SortKey; label: string; className?: string; tooltip?: string }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-700 ${className}`}
      onClick={() => handleSort(col)}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortKey === col && (
          <span className="text-[#dc2626]">{sortDir === 'asc' ? '↑' : '↓'}</span>
        )}
        {tooltip && <ColTooltip text={tooltip} />}
      </span>
    </th>
  );

  // Status distribution counts
  const counts = STATUSES.filter(s => s !== 'all').map(s => ({
    status: s,
    n: rows.filter(r => r.status === s).length,
    colour: rows.find(r => r.status === s)?.colour ?? '#94a3b8',
  }));

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
            <Settings className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Impact Score</h1>
            <p className="text-sm text-gray-500">Impact View + periodicity + spectral assessment — {rows.length} machines</p>
          </div>
        </div>

        {/* Status distribution pills */}
        <div className="flex flex-wrap items-center gap-1.5">
          {counts.map(c => (
            <button
              key={c.status}
              onClick={() => setStatusFilter(statusFilter === c.status ? 'all' : c.status)}
              className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-all ${
                statusFilter === c.status
                  ? 'text-white border-transparent'
                  : 'text-gray-600 border-gray-200 bg-white hover:border-gray-300'
              }`}
              style={statusFilter === c.status ? { backgroundColor: c.colour, borderColor: c.colour } : {}}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusFilter === c.status ? 'white' : c.colour }} />
              {c.status} ({c.n})
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        {databases.length > 1 && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Database:</span>
            {databases.map(([id, name]) => (
              <button
                key={id}
                onClick={() => setDbFilter(dbFilter === id ? null : id)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                  dbFilter === id
                    ? 'bg-gray-800 text-white border-gray-800'
                    : 'text-gray-600 border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}
        {(statusFilter !== 'all' || dbFilter !== null) && (
          <button
            onClick={() => { setStatusFilter('all'); setDbFilter(null); }}
            className="text-xs text-gray-400 hover:text-gray-600 underline"
          >
            Clear filters
          </button>
        )}
        <span className="text-xs text-gray-400 ml-auto">{sorted.length} shown</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6">
            <DataUnavailable message="Bearing health could not be loaded." />
          </div>
        ) : sorted.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center text-gray-400 gap-2">
            <Settings className="w-8 h-8 opacity-40" />
            <p className="text-sm">No results for current filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <SortHeader col="machine_name" label="Machine" />
                  <SortHeader col="status" label="Impact Status" />
                  <SortHeader col="worst_maxpk_g" label="Latest MaxPk"
                    tooltip="Most recent PeakVue stress wave amplitude reading. Color reflects machine-specific calibrated thresholds — moderate and severe levels are derived from each axis's historical baseline. Machines with sufficient history (≥10 readings) use statistical thresholds; others fall back to site defaults." />
                  <SortHeader col="periodic_avg_pct" label="Periodicity %"
                    tooltip="How repetitive the impact pattern is. High values (≥30%) suggest a structured bearing fault rather than random noise. ≥50% is strongly fault-like." />
                  <SortHeader col="latest_concentration" label="Concentration"
                    tooltip="Fraction of impact energy concentrated in specific frequency bands vs. broadband noise. Higher values point to focused fault energy at a repeating source. From the most recent TWF reading." />
                  <SortHeader col="latest_harmonic_fraction" label="Harmonics"
                    tooltip="Fraction of spectral energy organized into a harmonic series. High values confirm a structured, repeating fault source — a key indicator of progressive bearing damage. From the most recent TWF reading." />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    <span className="flex items-center gap-1">
                      Harmonic Series
                      <ColTooltip text="Fundamental CPM where the harmonic comb begins. Color = rate of change of harmonic energy over ~30 days: red rising, amber falling (late-stage or improving — verify), gray stable. Requires 6+ running captures. Bar shows energy vs this machine's own historical median." />
                    </span>
                  </th>
                  <SortHeader col="confidence" label="Confidence"
                    tooltip="Reliability of the assessment. Based on the number of PeakVue waveform readings available on the worst axis in the last 30 days. High = ≥10 readings · Medium = 5–9 · Low = <5." />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Summary</th>
                  <SortHeader col="calculated_at" label="Updated" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sorted.map(row => (
                  <tr
                    key={row.machine_id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => onMachineClick(row.machine_id, row.machine_name, [], undefined, row.latest_point_name ?? undefined)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{row.machine_name}</div>
                      <div className="text-xs text-gray-400">{row.company_name} › {row.area_name}</div>
                      {row.latest_point_name && (
                        <div className="text-xs text-blue-600 mt-0.5 font-medium">
                          {row.latest_point_name}{row.latest_axis_num ? ` (${AXIS_LABEL[row.latest_axis_num] ?? row.latest_axis_num})` : ''}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        // W5 (I-13): gate pill color on recency of the derived-table
                        // compute — a stale bearing_health row must not read as its
                        // health color (green/red). Stale/disconnected ⇒ neutral gray.
                        const pillColor = resolveIndicatorColor(
                          row.colour,
                          freshnessStatus(row.calculated_at, {
                            cadenceMs: CADENCE.dailyCompute,
                            staleMult: 2,
                            disconnectedMult: 4,
                          }).status,
                        ).color;
                        return (
                          <span
                            className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full text-white"
                            style={{ backgroundColor: pillColor }}
                          >
                            {row.status === 'Healthy'
                              ? '✓'
                              : <Settings className="w-2.5 h-2.5" />
                            }
                            {row.status}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      {row.latest_maxpk_g != null ? (() => {
                        const roc = maxpkRoc[row.machine_id];
                        const rocState = roc?.roc_state;
                        const tip = roc && roc.slope_pct_wk != null
                          ? `MaxPk trajectory: ${roc.slope_pct_wk >= 0 ? '+' : ''}${roc.slope_pct_wk}%/wk over 90d (r²=${roc.r2}, n=${roc.n_captures})`
                          : rocState === 'insufficient'
                            ? `MaxPk trajectory: too few captures to trend (${roc?.n_captures ?? 0} qualifying)`
                            : undefined;
                        return (
                          <span className="inline-flex items-center gap-1" title={tip}>
                            <span className={`text-xs font-medium ${
                              row.latest_maxpk_severity === 'urgent'      ? 'text-red-600'
                            : row.latest_maxpk_severity === 'action'      ? 'text-orange-600'
                            : row.latest_maxpk_severity === 'investigate' ? 'text-yellow-600'
                            : row.latest_maxpk_severity === 'monitor'     ? 'text-amber-500'
                            : 'text-gray-600'
                            }`}>
                              {row.latest_maxpk_g.toFixed(1)}G
                            </span>
                            {(rocState === 'rising' || rocState === 'rising_fast') &&
                              <span className="text-red-600 text-xs leading-none">▲</span>}
                            {rocState === 'falling' &&
                              <span className="text-green-600 text-xs leading-none">▼</span>}
                          </span>
                        );
                      })()
                      : <span className="text-xs text-gray-400">—</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {row.periodic_avg_pct != null
                        ? <span className={`font-medium ${row.periodic_avg_pct >= 50 ? 'text-red-600' : row.periodic_avg_pct >= 30 ? 'text-orange-500' : 'text-gray-600'}`}>
                            {row.periodic_avg_pct.toFixed(0)}%
                          </span>
                        : <span className="text-gray-400">—</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {row.latest_concentration != null
                        ? <span className={`font-medium ${row.latest_concentration >= 0.4 ? 'text-red-600' : row.latest_concentration >= 0.2 ? 'text-orange-500' : 'text-gray-600'}`}>
                            {(row.latest_concentration * 100).toFixed(0)}%
                          </span>
                        : <span className="text-gray-400">—</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {row.latest_harmonic_fraction != null
                        ? <span className={`font-medium ${row.latest_harmonic_fraction >= 0.6 ? 'text-red-600' : row.latest_harmonic_fraction >= 0.4 ? 'text-orange-500' : 'text-gray-600'}`}>
                            {(row.latest_harmonic_fraction * 100).toFixed(0)}%
                          </span>
                        : <span className="text-gray-400">—</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      {harmonicColumn[row.machine_id]
                        ? <HarmonicSeriesCell data={harmonicColumn[row.machine_id]} />
                        : <span className="text-gray-300 text-xs">—</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      <ConfidencePip level={row.confidence} />
                    </td>
                    <td
                      className="px-4 py-3 max-w-xs"
                      onClick={e => { e.stopPropagation(); if (row.summary_line) toggleSummary(row.machine_id); }}
                    >
                      {row.summary_line ? (
                        <>
                          <span className={`text-xs text-gray-600 ${expandedSummaries.has(row.machine_id) ? '' : 'line-clamp-2'}`}>
                            {row.summary_line}
                          </span>
                          <button
                            className="text-[10px] text-blue-500 hover:text-blue-700 mt-0.5 block leading-none"
                            onClick={e => { e.stopPropagation(); toggleSummary(row.machine_id); }}
                          >
                            {expandedSummaries.has(row.machine_id) ? '▲ less' : '▼ more'}
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                      {row.mitigating && (
                        <div className="text-xs text-green-600 mt-0.5">✓ {row.mitigating}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                      {fmtUtcToEastern(row.calculated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
